/**
 * Pure helpers for the calendar views (filtering, RBC mapping, preferences).
 */
import * as React from 'react';
import * as Icons from 'lucide-react';
import { dateFnsLocalizer, type View } from 'react-big-calendar';
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  parse,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import type {
  CalendarEvent,
  CalendarEventType,
  CalendarParticipant,
  CalendarPrefs,
  CalendarRbcEvent,
} from '@/types/calendar.types';
import { getRoleLabel, getRoleLabelPlural, roleKind } from '@/utils/role-label-utils';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import {
  CALENDAR_ALL_ZONES_LABEL,
  CALENDAR_ANIMATOR_ROLE_KINDS,
  CALENDAR_CLUSTER_MAX_VISIBLE,
  CALENDAR_EVENT_TYPE_PRIORITY,
  CALENDAR_EVENT_STATUS_CONFIG,
  CALENDAR_FIELD_EVENT_SESSION_TYPE,
  CALENDAR_INFORMATION_MEETING_TYPES,
  CALENDAR_VIEWS,
  CALENDAR_WELCOME_MEETING_TYPE,
  DEFAULT_CALENDAR_EVENT_TYPES,
  EVENT_COLOR_MAP,
  EVENT_TYPE_CONFIG,
  FIELD_EVENT_ORGANIZER_ROLES,
  HOLIDAY_TYPE_LABELS,
  IN_PERSON_INFO_MEETING_COMPLETED_LABEL,
  POSTIT_STATUS_CONFIG,
  SESSION_REMOTE_TYPE_CONFIG,
  SESSION_STATUS_CONFIG,
  SESSION_TYPE_CONFIG,
  THEMATIC_LABELS,
  type CalendarStatusChip,
  type EventTypeIcons,
  type SessionTypeConfig,
} from '@/components/calendar/calendar-configs';

/** French locale bag for react-big-calendar. */
const calendarLocales = {
  fr,
};

/** date-fns localizer shared by the full calendar instance. */
export const calendarLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: calendarLocales,
});

/** Upcoming event, otherwise the most recent past one. */
function pickUpcomingOrLatest(events: CalendarEvent[], now: Date): CalendarEvent {
  const upcoming = events
    .filter((event) => event.startDate >= now)
    .sort((left, right) => left.startDate.getTime() - right.startDate.getTime());
  if (upcoming.length > 0) return upcoming[0];
  return [...events].sort((left, right) => right.startDate.getTime() - left.startDate.getTime())[0];
}

/** First session on the cell, upcoming before past. */
function pickSessionPrimary(events: CalendarEvent[], now: Date): CalendarEvent | null {
  const sessions = events.filter((event) => event.type === 'session');
  if (sessions.length === 0) return null;
  return pickUpcomingOrLatest(sessions, now);
}

/** Open post-it on the cell; priority post-its win. Done post-its are skipped. */
function pickOpenPostitPrimary(events: CalendarEvent[]): CalendarEvent | null {
  const postits = events.filter(
    (event) => event.type === 'postit' && event.metadata.postitStatus !== 'done',
  );
  if (postits.length === 0) return null;
  const priority = postits
    .filter((event) => event.metadata.postitPriority === true)
    .sort((left, right) => left.startDate.getTime() - right.startDate.getTime());
  if (priority.length > 0) return priority[0];
  const rest = postits
    .filter((event) => event.metadata.postitPriority !== true)
    .sort((left, right) => left.startDate.getTime() - right.startDate.getTime());
  return rest[0] ?? null;
}

/** `YYYY-M-D` key using local calendar parts (month is 0-based). */
function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** Every local day touched by the event list, expanding multi-day all-day spans. */
function collectCalendarDateKeys(events: CalendarEvent[]): Set<string> {
  const keys = new Set<string>();
  for (const event of events) {
    const spansSeveralDays = event.allDay && event.startDate.getTime() !== event.endDate.getTime();
    if (!spansSeveralDays) {
      keys.add(toDateKey(event.startDate));
      continue;
    }
    const current = new Date(event.startDate);
    const end = new Date(event.endDate);
    while (current <= end) {
      keys.add(toDateKey(current));
      current.setDate(current.getDate() + 1);
    }
  }
  return keys;
}

/** How the session modal splits participants (RI / RA / terrain vs tutoring). */
export type CalendarSessionParticipantLayout =
  | 'tutoring'
  | 'information_meeting'
  | 'welcome_meeting'
  | 'field_event';

/** Column data for the session modal participant block. */
export interface CalendarSessionParticipantColumns {
  layout: CalendarSessionParticipantLayout;
  leftLabel: string;
  rightLabel: string | null;
  left: CalendarParticipant[];
  right: CalendarParticipant[];
}

/** Tutor-column header: tutors only when remote; tutors + VSC when in-person. */
export function getCalendarSessionTutorColumnLabel(sessionFormat?: string): string {
  if (sessionFormat === 'remote') {
    return getRoleLabelPlural('tutor');
  }
  return `${getRoleLabelPlural('tutor')} / ${getRoleLabel('volunteer')}`;
}

/** Whether a participant belongs in the session popup tutor column. */
export function isCalendarTutorColumnParticipant(role: string, sessionFormat?: string): boolean {
  const kind = roleKind(role);
  if (sessionFormat === 'remote') {
    return kind === 'tutor';
  }
  return kind === 'tutor' || kind === 'volunteer' || kind === 'recruiter' || kind === 'coordinator';
}

/** Whether a participant belongs in the session popup student column. */
export function isCalendarStudentColumnParticipant(role: string): boolean {
  const kind = roleKind(role);
  return kind === 'student' || kind === 'parent';
}

/** Whether a participant is stored as an RI / RA / terrain animator. */
export function isCalendarAnimatorParticipant(role: string): boolean {
  return CALENDAR_ANIMATOR_ROLE_KINDS.has(roleKind(role));
}

/** Participant layout for a session type (remote workshop = field event). */
export function getCalendarSessionParticipantLayout(
  sessionType?: string,
  mode?: string | null,
): CalendarSessionParticipantLayout {
  if (sessionType && CALENDAR_INFORMATION_MEETING_TYPES.has(sessionType)) {
    return 'information_meeting';
  }
  if (sessionType === CALENDAR_WELCOME_MEETING_TYPE) {
    return 'welcome_meeting';
  }
  if (sessionType === CALENDAR_FIELD_EVENT_SESSION_TYPE && mode === 'remote') {
    return 'field_event';
  }
  return 'tutoring';
}

/** Column headers for the session modal participant block. */
export function getCalendarSessionParticipantColumnLabels(
  layout: CalendarSessionParticipantLayout,
  sessionFormat?: string,
): { leftLabel: string; rightLabel: string | null } {
  const animatorLabel = getRoleLabelPlural('animator');
  if (layout === 'information_meeting') {
    return { leftLabel: animatorLabel, rightLabel: getRoleLabelPlural('tutor') };
  }
  if (layout === 'welcome_meeting') {
    return { leftLabel: animatorLabel, rightLabel: getRoleLabelPlural('student') };
  }
  if (layout === 'field_event') {
    return { leftLabel: animatorLabel, rightLabel: null };
  }
  return {
    leftLabel: getCalendarSessionTutorColumnLabel(sessionFormat),
    rightLabel: getRoleLabelPlural('student'),
  };
}

/** Split participants into the two modal columns for a given layout. */
export function splitSessionParticipantsByLayout(
  participants: CalendarParticipant[],
  layout: CalendarSessionParticipantLayout,
  sessionFormat?: string,
): { left: CalendarParticipant[]; right: CalendarParticipant[] } {
  if (layout === 'information_meeting') {
    return {
      left: participants.filter((participant) => isCalendarAnimatorParticipant(participant.role)),
      right: participants.filter((participant) => roleKind(participant.role) === 'tutor'),
    };
  }
  if (layout === 'welcome_meeting') {
    return {
      left: participants.filter((participant) => isCalendarAnimatorParticipant(participant.role)),
      right: participants.filter((participant) =>
        isCalendarStudentColumnParticipant(participant.role),
      ),
    };
  }
  if (layout === 'field_event') {
    return {
      left: participants.filter((participant) => isCalendarAnimatorParticipant(participant.role)),
      right: [],
    };
  }
  return {
    left: participants.filter((participant) =>
      isCalendarTutorColumnParticipant(participant.role, sessionFormat),
    ),
    right: participants.filter((participant) =>
      isCalendarStudentColumnParticipant(participant.role),
    ),
  };
}

/** Layout + labels + split columns for the session modal participant block. */
export function getCalendarSessionParticipantColumns(
  participants: CalendarParticipant[],
  sessionType?: string,
  sessionFormat?: string,
  mode?: string | null,
): CalendarSessionParticipantColumns {
  const layout = getCalendarSessionParticipantLayout(sessionType, mode);
  const labels = getCalendarSessionParticipantColumnLabels(layout, sessionFormat);
  const split = splitSessionParticipantsByLayout(participants, layout, sessionFormat);
  return { layout, ...labels, ...split };
}

/** Split session participants into the tutor column and the student column. */
export function splitSessionParticipants(
  participants: CalendarParticipant[],
  sessionFormat?: string,
): { tutors: CalendarParticipant[]; students: CalendarParticipant[] } {
  const columns = getCalendarSessionParticipantColumns(
    participants,
    undefined,
    sessionFormat,
    null,
  );
  return { tutors: columns.left, students: columns.right };
}

/** Split field-event participants into organizers and registered students. */
export function splitFieldEventParticipants(participants: CalendarParticipant[]): {
  organizers: CalendarParticipant[];
  students: CalendarParticipant[];
} {
  return {
    organizers: participants.filter((participant) =>
      FIELD_EVENT_ORGANIZER_ROLES.has(participant.role),
    ),
    students: participants.filter((participant) => participant.role === 'student'),
  };
}

/** Session status chip; in-person information meetings show "Réalisée" once completed. */
export function getSessionStatusChip(
  sessionType?: string,
  sessionStatus?: string,
): CalendarStatusChip {
  const displayStatus = sessionStatus === 'PARTICIPANTS_ABSENT' ? 'NOT_COMPLETED' : sessionStatus;
  const statusConfig = SESSION_STATUS_CONFIG[displayStatus ?? ''] ?? SESSION_STATUS_CONFIG.PLANNED;
  if (sessionType === 'information_meeting_in_person' && sessionStatus === 'COMPLETED') {
    return { ...statusConfig, label: IN_PERSON_INFO_MEETING_COMPLETED_LABEL };
  }
  return statusConfig;
}

/** Field-event status chip, falling back to planned. */
export function getFieldEventStatusChip(eventStatus?: string): CalendarStatusChip {
  return CALENDAR_EVENT_STATUS_CONFIG[eventStatus ?? ''] ?? CALENDAR_EVENT_STATUS_CONFIG.PLANNED;
}

/** Post-it status chip, falling back to in progress. */
export function getPostitStatusChip(postitStatus?: string): CalendarStatusChip {
  return POSTIT_STATUS_CONFIG[postitStatus ?? ''] ?? POSTIT_STATUS_CONFIG.en_cours;
}

/** Static thematic label, or the live role label for `tuteur` / `eleves`. */
export function getThematicLabel(thematic: string | undefined): string | undefined {
  if (!thematic) return thematic;
  if (thematic === 'tuteur') return getRoleLabel('tutor');
  if (thematic === 'eleves') return getRoleLabelPlural('student');
  return THEMATIC_LABELS[thematic] ?? thematic;
}

/** Holiday-type label, falling back to the raw API enum. */
export function getHolidayTypeLabel(holidayType: string | undefined): string | undefined {
  if (!holidayType) return holidayType;
  return HOLIDAY_TYPE_LABELS[holidayType] ?? holidayType;
}

/**
 * Resolves calendar event-type icons dynamically from the sidebar interface elements,
 * so that changing a menu icon in the DB is automatically reflected in the calendar.
 */
export function useEventTypeIcons(): EventTypeIcons {
  const { elements } = useInterfaceElements('sidebar');

  return React.useMemo(() => {
    const iconsMap = Icons as unknown as Record<string, Icons.LucideIcon>;

    function resolve(menuId: string | undefined, fallback: string): Icons.LucideIcon {
      if (menuId) {
        const element = elements.find((item) => item.elementId === menuId);
        const name = element?.config?.iconName as string | undefined;
        if (name && iconsMap[name]) return iconsMap[name];
      }
      return iconsMap[fallback] ?? Icons.Circle;
    }

    return {
      session: resolve(
        EVENT_TYPE_CONFIG.session.sidebarMenuId,
        EVENT_TYPE_CONFIG.session.fallbackIconName,
      ),
      event: resolve(
        EVENT_TYPE_CONFIG.event.sidebarMenuId,
        EVENT_TYPE_CONFIG.event.fallbackIconName,
      ),
      postit: resolve(undefined, EVENT_TYPE_CONFIG.postit.fallbackIconName),
      holiday: resolve(
        EVENT_TYPE_CONFIG.holiday.sidebarMenuId,
        EVENT_TYPE_CONFIG.holiday.fallbackIconName,
      ),
    };
  }, [elements]);
}

/** Keep national holidays (no zone) plus holidays matching the selected vacation zone. */
export function filterCalendarEventsByVacationZone(
  events: CalendarEvent[],
  vacationZoneId?: string,
): CalendarEvent[] {
  if (!vacationZoneId) return events;

  return events.filter((event) => {
    if (event.type !== 'holiday') return true;
    const zoneId = event.metadata.vacationZoneId;
    return zoneId == null || zoneId === vacationZoneId;
  });
}

/** Events whose type is still enabled in the filter chips. */
export function filterCalendarEventsByType(
  events: CalendarEvent[],
  enabledTypes: readonly CalendarEventType[],
): CalendarEvent[] {
  return events.filter((event) => enabledTypes.includes(event.type));
}

/** Events that start on `day`, plus all-day events that span it. */
export function getEventsOnDay(events: CalendarEvent[], day: Date): CalendarEvent[] {
  const year = day.getFullYear();
  const month = day.getMonth();
  const date = day.getDate();

  return events.filter((event) => {
    const start = event.startDate;
    if (start.getFullYear() === year && start.getMonth() === month && start.getDate() === date) {
      return true;
    }
    return event.allDay && event.startDate <= day && event.endDate >= day;
  });
}

/** Type priority, then start time. Does not mutate the input. */
export function sortCalendarEvents(events: CalendarEvent[]): CalendarEvent[] {
  return [...events].sort((left, right) => {
    const priorityDelta =
      CALENDAR_EVENT_TYPE_PRIORITY[left.type] - CALENDAR_EVENT_TYPE_PRIORITY[right.type];
    if (priorityDelta !== 0) return priorityDelta;
    return left.startDate.getTime() - right.startDate.getTime();
  });
}

/**
 * One event per full-calendar day cell: session, then open post-it, then holiday.
 * Done post-its never steal the cell color.
 */
export function getFullCalendarPrimaryEvent(
  events: CalendarEvent[],
  activeTypes: readonly CalendarEventType[],
  now: Date = new Date(),
): CalendarEvent | null {
  if (events.length === 0) return null;
  const activeEvents = events.filter((event) => activeTypes.includes(event.type));
  if (activeEvents.length === 0) return null;
  if (activeTypes.includes('session')) {
    const session = pickSessionPrimary(activeEvents, now);
    if (session) return session;
  }
  if (activeTypes.includes('postit')) {
    const postit = pickOpenPostitPrimary(activeEvents);
    if (postit) return postit;
  }
  if (activeTypes.includes('holiday')) {
    return activeEvents.find((event) => event.type === 'holiday') ?? null;
  }
  return null;
}

/**
 * Month `+N` list: events on the day that are not already drawn in the cell.
 * A day with no primary block keeps every event only when several share the cell.
 */
export function getMonthOverflowEvents(
  events: CalendarEvent[],
  activeTypes: readonly CalendarEventType[],
  now: Date = new Date(),
): CalendarEvent[] {
  const primary = getFullCalendarPrimaryEvent(events, activeTypes, now);
  if (!primary) return events.length > 1 ? events : [];
  return events.filter((event) => event !== primary);
}

/** Remote catalog when `sessionFormat` is remote, otherwise in-person, then the other. */
export function resolveCalendarSessionTypeConfig(
  sessionType?: string,
  sessionFormat?: string,
  mode?: string | null,
): SessionTypeConfig | undefined {
  if (!sessionType) return undefined;
  if (sessionType === CALENDAR_FIELD_EVENT_SESSION_TYPE && mode === 'remote') {
    return SESSION_REMOTE_TYPE_CONFIG.workshop;
  }
  if (sessionFormat === 'remote') {
    return SESSION_REMOTE_TYPE_CONFIG[sessionType] ?? SESSION_TYPE_CONFIG[sessionType];
  }
  return SESSION_TYPE_CONFIG[sessionType] ?? SESSION_REMOTE_TYPE_CONFIG[sessionType];
}

/** RBC / agenda chip title: session-kind label when the metadata carries one. */
export function getCalendarEventDisplayTitle(event: CalendarEvent): string {
  if (event.type === 'session' && event.metadata.sessionType) {
    return (
      resolveCalendarSessionTypeConfig(event.metadata.sessionType, event.metadata.sessionFormat)
        ?.label || event.title
    );
  }
  return event.title;
}

/**
 * RBC feed: one primary event per calendar day (multi-day all-day events expand into each day).
 * Ids are suffixed with the date key so the same source can appear on several cells.
 */
export function buildPrimaryRbcEvents(
  events: CalendarEvent[],
  enabledTypes: readonly CalendarEventType[],
): CalendarRbcEvent[] {
  const primaryEvents: CalendarEvent[] = [];

  for (const dateKey of collectCalendarDateKeys(events)) {
    const [year, month, day] = dateKey.split('-').map(Number);
    const targetDate = new Date(year, month, day);
    const primaryEvent = getFullCalendarPrimaryEvent(
      getEventsOnDay(events, targetDate),
      enabledTypes,
    );
    if (!primaryEvent) continue;

    primaryEvents.push({
      ...primaryEvent,
      id: `${primaryEvent.id}-${dateKey}`,
      startDate: primaryEvent.allDay
        ? primaryEvent.startDate
        : new Date(
            year,
            month,
            day,
            primaryEvent.startDate.getHours(),
            primaryEvent.startDate.getMinutes(),
          ),
      endDate: primaryEvent.allDay
        ? primaryEvent.endDate
        : new Date(
            year,
            month,
            day,
            primaryEvent.endDate.getHours(),
            primaryEvent.endDate.getMinutes(),
          ),
    });
  }

  return primaryEvents.map(toRbcEvent);
}

/** Map one calendar event to an RBC row. */
function toRbcEvent(event: CalendarEvent): CalendarRbcEvent {
  return {
    id: event.id,
    title: getCalendarEventDisplayTitle(event),
    start: event.startDate,
    end: event.endDate,
    allDay: event.allDay,
    resource: event,
  };
}

/** Local hour window (`H:00` → next hour, or end of day at 23). */
function hourBucketRange(day: Date, hour: number): { start: Date; end: Date } {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, 0, 0, 0);
  const end =
    hour >= 23
      ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999)
      : new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour + 1, 0, 0, 0);
  return { start, end };
}

/** Group events by all-day day key and by timed start-hour key (`YYYY-M-D-H`). */
function groupEventsByStartHour(events: CalendarEvent[]): {
  allDayBuckets: Map<string, CalendarEvent[]>;
  hourBuckets: Map<string, CalendarEvent[]>;
} {
  const allDayBuckets = new Map<string, CalendarEvent[]>();
  const hourBuckets = new Map<string, CalendarEvent[]>();

  for (const event of events) {
    if (event.allDay) {
      const key = toDateKey(event.startDate);
      const bucket = allDayBuckets.get(key) ?? [];
      bucket.push(event);
      allDayBuckets.set(key, bucket);
      continue;
    }
    const start = event.startDate;
    const key = `${toDateKey(start)}-${start.getHours()}`;
    const bucket = hourBuckets.get(key) ?? [];
    bucket.push(event);
    hourBuckets.set(key, bucket);
  }

  return { allDayBuckets, hourBuckets };
}

/** Cap one start-hour bucket to `maxVisible` RBC blocks (no grid `+K` row). */
function emitHourBucketVisibleRbcEvents(
  bucket: CalendarEvent[],
  maxVisible: number,
): CalendarRbcEvent[] {
  return sortCalendarEvents(bucket).slice(0, maxVisible).map(toRbcEvent);
}

/** Hidden events from one start-hour (or all-day) bucket after the visible cap. */
function hiddenEventsFromBucket(bucket: CalendarEvent[], maxVisible: number): CalendarEvent[] {
  const ranked = sortCalendarEvents(bucket);
  return ranked.length > maxVisible ? ranked.slice(maxVisible) : [];
}

/**
 * Day / week feed: start-hour buckets, at most `maxVisible` blocks per hour.
 * Overflow is shown as a header `+K` under the date, not in the grid.
 */
export function buildClusteredRbcEvents(
  events: CalendarEvent[],
  maxVisible: number,
): CalendarRbcEvent[] {
  const { allDayBuckets, hourBuckets } = groupEventsByStartHour(events);
  const rows: CalendarRbcEvent[] = [];

  for (const [, bucket] of [...allDayBuckets.entries()].sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    rows.push(...emitHourBucketVisibleRbcEvents(bucket, maxVisible));
  }

  for (const [, bucket] of [...hourBuckets.entries()].sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    rows.push(...emitHourBucketVisibleRbcEvents(bucket, maxVisible));
  }

  return rows;
}

/**
 * Events hidden by the per-hour cap on a given day (for the day/week header `+K`).
 */
export function getTimeGridHiddenEventsOnDay(
  events: CalendarEvent[],
  day: Date,
  maxVisible: number,
): CalendarEvent[] {
  const { allDayBuckets, hourBuckets } = groupEventsByStartHour(getEventsOnDay(events, day));
  const hidden: CalendarEvent[] = [];
  for (const bucket of allDayBuckets.values()) {
    hidden.push(...hiddenEventsFromBucket(bucket, maxVisible));
  }
  for (const bucket of hourBuckets.values()) {
    hidden.push(...hiddenEventsFromBucket(bucket, maxVisible));
  }
  return hidden;
}

/** One agenda row packing a sorted bucket (chips in the UI, optional `+K`). */
function buildAgendaLaneRbcEvent(
  bucket: CalendarEvent[],
  start: Date,
  end: Date,
  allDay: boolean,
  bucketKey: string,
): CalendarRbcEvent {
  const ranked = sortCalendarEvents(bucket);
  const anchor = ranked[0];
  return {
    id: `agenda-lane-${bucketKey}`,
    title: getCalendarEventDisplayTitle(anchor),
    start,
    end,
    allDay,
    resource: anchor,
    laneEvents: ranked,
  };
}

/**
 * Agenda feed: one row per start-hour (or all-day day); chips + `+K` are rendered from `laneEvents`.
 */
export function buildAgendaHourBucketRbcEvents(events: CalendarEvent[]): CalendarRbcEvent[] {
  const { allDayBuckets, hourBuckets } = groupEventsByStartHour(events);
  const rows: CalendarRbcEvent[] = [];

  for (const [dateKey, bucket] of [...allDayBuckets.entries()].sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    const [year, month, day] = dateKey.split('-').map(Number);
    const start = new Date(year, month, day);
    rows.push(buildAgendaLaneRbcEvent(bucket, start, start, true, `allday-${dateKey}`));
  }

  for (const [bucketKey, bucket] of [...hourBuckets.entries()].sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    const anchor = bucket[0].startDate;
    const { start, end } = hourBucketRange(anchor, anchor.getHours());
    rows.push(buildAgendaLaneRbcEvent(bucket, start, end, false, bucketKey));
  }

  return rows;
}

/** True when the RBC row is an agenda hour/day lane packing several events. */
export function isCalendarAgendaLaneRbcEvent(event: CalendarRbcEvent): boolean {
  return (event.laneEvents?.length ?? 0) > 0;
}

/** First chips shown in an agenda lane, plus how many hide behind `+K`. */
export function splitAgendaLaneEvents(
  laneEvents: CalendarEvent[],
  maxVisible: number = CALENDAR_CLUSTER_MAX_VISIBLE.agenda,
): { visible: CalendarEvent[]; hiddenCount: number } {
  return {
    visible: laneEvents.slice(0, maxVisible),
    hiddenCount: Math.max(0, laneEvents.length - maxVisible),
  };
}

/** Pick month primary, agenda hour lanes, or day/week per-hour capped blocks. */
export function buildRbcEventsForView(
  events: CalendarEvent[],
  enabledTypes: readonly CalendarEventType[],
  view: View,
): CalendarRbcEvent[] {
  if (view === 'month') return buildPrimaryRbcEvents(events, enabledTypes);
  if (view === 'agenda') return buildAgendaHourBucketRbcEvents(events);
  if (view === 'week' || view === 'work_week') {
    return buildClusteredRbcEvents(events, CALENDAR_CLUSTER_MAX_VISIBLE.week);
  }
  return buildClusteredRbcEvents(events, CALENDAR_CLUSTER_MAX_VISIBLE.day);
}

/** RBC block colors from {@link EVENT_COLOR_MAP} by event type. */
export function getCalendarEventBlockStyle(event: { resource: CalendarEvent }): {
  style: { background: string; borderColor: string; color: string };
} {
  const colors = EVENT_COLOR_MAP[event.resource.type] || EVENT_COLOR_MAP.session;
  return {
    style: {
      background: colors.bg,
      borderColor: colors.border,
      color: 'white',
    },
  };
}

/** Coerce a persisted preference string to a react-big-calendar view (fallback: month). */
export function parseCalendarView(value?: string): View {
  if (value !== undefined && CALENDAR_VIEWS.has(value as View)) {
    return value as View;
  }
  return 'month';
}

/**
 * Map a month-grid cell (day number + off-range) back to a real Date.
 * Off-range days at the start of the grid belong to the previous month; those at the end to the next.
 */
export function resolveCellCalendarDate(
  visibleDate: Date,
  dayNumber: number,
  isOffRange: boolean,
): Date {
  let cellMonth = visibleDate.getMonth();
  let cellYear = visibleDate.getFullYear();

  if (!isOffRange) {
    return new Date(cellYear, cellMonth, dayNumber);
  }

  if (dayNumber > 20) {
    cellMonth -= 1;
    if (cellMonth < 0) {
      cellMonth = 11;
      cellYear -= 1;
    }
  } else if (dayNumber < 15) {
    cellMonth += 1;
    if (cellMonth > 11) {
      cellMonth = 0;
      cellYear += 1;
    }
  }

  return new Date(cellYear, cellMonth, dayNumber);
}

/** Accessible label for the `+N` overflow badge (French plural). */
export function additionalEventsAriaLabel(count: number): string {
  const plural = count > 1 ? 's' : '';
  return `${count} événement${plural} supplémentaire${plural}`;
}

/** Visible day/week overflow button copy: `+3 événements`. */
export function additionalEventsButtonLabel(count: number): string {
  const plural = count > 1 ? 's' : '';
  return `+${count} événement${plural}`;
}

/** Imperative `+N` badge: typed pill (month) or outlined blue button (day/week header). */
export function createDayEventsBadge(
  additionalCount: number,
  eventType: CalendarEventType | undefined,
  onClick: (event: Event) => void,
  tone: 'typed' | 'neutral' = 'typed',
): HTMLButtonElement {
  const badge = document.createElement('button');
  badge.type = 'button';
  badge.className =
    tone === 'neutral' ? 'rbc-date-cell-badge rbc-date-cell-badge--neutral' : 'rbc-date-cell-badge';
  badge.textContent =
    tone === 'neutral' ? additionalEventsButtonLabel(additionalCount) : `+${additionalCount}`;
  if (tone === 'typed') {
    badge.style.background = EVENT_TYPE_CONFIG[eventType ?? 'session']?.color;
  }
  const label = additionalEventsAriaLabel(additionalCount);
  badge.setAttribute('aria-label', label);
  badge.title = label;
  badge.addEventListener('click', onClick);
  return badge;
}

/** Monday, same as the French month grid (grayed leading and trailing days included). */
const CALENDAR_WEEK_STARTS_ON = 1;

/** react-big-calendar agenda length (days after the focused date). */
const CALENDAR_AGENDA_LENGTH_DAYS = 30;

/** Column dates for the week header, Monday-first. */
export function timeGridHeaderDates(focusDate: Date, columnCount: number): Date[] {
  const start = startOfWeek(focusDate, { weekStartsOn: CALENDAR_WEEK_STARTS_ON });
  return Array.from({ length: columnCount }, (_, index) => addDays(start, index));
}

/** Agenda date cells follow days that paint a primary event, in order. */
export function agendaPrimaryDates(
  events: CalendarEvent[],
  enabledTypes: readonly CalendarEventType[],
  focusDate: Date,
): Date[] {
  const start = startOfDay(focusDate);
  const end = addDays(start, CALENDAR_AGENDA_LENGTH_DAYS);
  const dates: Date[] = [];
  for (let cursor = start; cursor.getTime() <= end.getTime(); cursor = addDays(cursor, 1)) {
    const dayEvents = getEventsOnDay(events, cursor);
    if (getFullCalendarPrimaryEvent(dayEvents, enabledTypes)) {
      dates.push(cursor);
    }
  }
  return dates;
}

/** Copy the agenda body column widths onto the separate header row. */
export function alignAgendaColumnTitles(root: Element): void {
  const headerCells = root.querySelectorAll(
    '.rbc-agenda-view > .rbc-agenda-table > thead > tr > th',
  );
  const timeCell = root.querySelector('.rbc-agenda-time-cell');
  if (headerCells.length < 2 || !(timeCell instanceof HTMLElement)) return;

  let dateWidth = 0;
  root.querySelectorAll('.rbc-agenda-date-cell').forEach((cell) => {
    if (cell instanceof HTMLElement) {
      dateWidth = Math.max(dateWidth, cell.offsetWidth);
    }
  });
  if (dateWidth <= 0) return;

  const dateHeader = headerCells[0];
  const timeHeader = headerCells[1];
  if (dateHeader instanceof HTMLElement) dateHeader.style.width = `${dateWidth}px`;
  if (timeHeader instanceof HTMLElement) timeHeader.style.width = `${timeCell.offsetWidth}px`;
}

/**
 * Fetch window for the days react-big-calendar actually paints.
 * Month includes leading and trailing off-range days. Week is those 7 days,
 * day is that day, agenda is the focused date plus {@link CALENDAR_AGENDA_LENGTH_DAYS}
 * (inclusive, same as RBC's default `length`).
 */
export function getCalendarFetchRange(
  focusDate: Date,
  view: View = 'month',
): { start: Date; end: Date } {
  const week = { weekStartsOn: CALENDAR_WEEK_STARTS_ON } as const;

  if (view === 'week' || view === 'work_week') {
    return {
      start: startOfWeek(focusDate, week),
      end: endOfWeek(focusDate, week),
    };
  }

  if (view === 'day') {
    const day = startOfDay(focusDate);
    return { start: day, end: day };
  }

  if (view === 'agenda') {
    const start = startOfDay(focusDate);
    return { start, end: addDays(start, CALENDAR_AGENDA_LENGTH_DAYS) };
  }

  return {
    start: startOfWeek(startOfMonth(focusDate), week),
    end: endOfWeek(endOfMonth(focusDate), week),
  };
}

/** Title-case French month names in a calendar toolbar label (`octobre` → `Octobre`). */
export function capitalizeFrenchMonthLabel(label: string): string {
  return label.replace(
    /\b(janvier|février|mars|avril|mai|juin|juillet|août|septembre|octobre|novembre|décembre|janv\.|févr\.|avr\.|juil\.|sept\.|oct\.|nov\.|déc\.)/gi,
    (month) => month.charAt(0).toLocaleUpperCase('fr-FR') + month.slice(1),
  );
}

/** Vacation-zone {@link Select} options, with an empty "all zones" row first. */
export function buildCalendarZoneOptions(
  zones: Array<{ id: string; name: string; code: string }>,
): Array<{ value: string; label: string }> {
  return [
    { value: '', label: CALENDAR_ALL_ZONES_LABEL },
    ...zones.map((zone) => ({
      value: zone.id,
      label: zone.name || zone.code,
    })),
  ];
}

/** Persist the zone filter; an empty string drops `vacationZoneId`. */
export function applyVacationZonePreference(prefs: CalendarPrefs, value: string): CalendarPrefs {
  const next: CalendarPrefs = {
    ...prefs,
    defaultView: prefs.defaultView || 'month',
    eventSources: prefs.eventSources || [...DEFAULT_CALENDAR_EVENT_TYPES],
  };
  if (value) {
    next.vacationZoneId = value;
  } else {
    delete next.vacationZoneId;
  }
  return next;
}

/** Persist the react-big-calendar view without dropping a selected vacation zone. */
export function applyCalendarViewPreference(prefs: CalendarPrefs, view: string): CalendarPrefs {
  return {
    ...prefs,
    defaultView: view,
    eventSources: prefs.eventSources || [...DEFAULT_CALENDAR_EVENT_TYPES],
    ...(prefs.vacationZoneId ? { vacationZoneId: prefs.vacationZoneId } : {}),
  };
}

/** Field events exist only in the in-person mode. */
export function calendarEventTypesForMode(
  types: readonly CalendarEventType[],
  mode: 'in_person' | 'remote' | null | undefined,
): CalendarEventType[] {
  if (mode !== 'remote') return [...types];
  return types.filter((type) => type !== 'event');
}

/** Toggle one event-type chip. */
export function toggleCalendarEventType(
  enabled: CalendarEventType[],
  type: CalendarEventType,
): CalendarEventType[] {
  if (enabled.includes(type)) {
    return enabled.filter((item) => item !== type);
  }
  return [...enabled, type];
}

/** Calendar prefs in the shape `saveConfig` accepts. */
export function toCalendarPreferenceRecord(prefs: CalendarPrefs): Record<string, unknown> {
  return prefs as Record<string, unknown>;
}
